import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { Observable, catchError, concatMap, from, map, of, tap, throwError, toArray } from 'rxjs';

import {
  AlertNotification,
  AlertNotificationResponse,
  AlertNotificationsResponse,
} from '../models';

@Injectable({ providedIn: 'root' })
export class AlertNotificationsService {
  private readonly http = inject(HttpClient);

  private readonly _alerts = signal<AlertNotification[]>([]);
  private readonly _loading = signal(false);

  readonly alerts: Signal<AlertNotification[]> = this._alerts.asReadonly();
  readonly loading: Signal<boolean> = this._loading.asReadonly();
  readonly unreadCount = computed(() => this._alerts().filter((alert) => !alert.seen).length);

  fetchAlerts({
    limit = 50,
    onlyUnread,
  }: { limit?: number; onlyUnread?: boolean } = {}): Observable<AlertNotification[]> {
    this._loading.set(true);

    let params = new HttpParams().set('limit', limit);
    if (onlyUnread !== undefined) {
      params = params.set('onlyUnread', String(onlyUnread));
    }

    return this.http
      .get<AlertNotificationsResponse>('notifications/alerts', { params })
      .pipe(
        map((response) => (response.alerts ?? []).map((alert) => this.normalize(alert))),
        tap((alerts) => this._alerts.set(alerts)),
        catchError((error) => this.handleError('No se pudieron cargar tus notificaciones.', error)),
        tap({
          error: () => this._loading.set(false),
          complete: () => this._loading.set(false),
        }),
      );
  }

  markAsSeen(alertId: string): Observable<AlertNotification> {
    return this.http
      .patch<AlertNotificationResponse>(`notifications/alerts/${alertId}/seen`, {})
      .pipe(
        map((response) => this.normalize(response.alert)),
        tap((updatedAlert) => this.updateAlert(updatedAlert)),
        catchError((error) => this.handleError('No se pudo marcar la notificación como leída.', error)),
      );
  }

  markAllAsSeen(): Observable<AlertNotification[]> {
    const unreadAlerts = this._alerts().filter((alert) => !alert.seen);
    if (unreadAlerts.length === 0) {
      return of(this._alerts());
    }

    return from(unreadAlerts).pipe(
      concatMap((alert) =>
        this.http
          .patch<AlertNotificationResponse>(`notifications/alerts/${alert.id}/seen`, {})
          .pipe(map((response) => this.normalize(response.alert)), tap((updated) => this.updateAlert(updated))),
      ),
      toArray(),
      map(() => this._alerts()),
      catchError((error) => this.handleError('No se pudieron marcar todas como leídas.', error)),
    );
  }

  reset(): void {
    this._alerts.set([]);
    this._loading.set(false);
  }

  private normalize(alert: AlertNotification): AlertNotification {
    const id = (alert as AlertNotification & { _id?: string }).id ?? (alert as { _id?: string })._id ?? '';

    return {
      ...alert,
      id,
    };
  }

  private updateAlert(updatedAlert: AlertNotification): void {
    this._alerts.update((alerts) =>
      alerts.map((alert) => (alert.id === updatedAlert.id ? updatedAlert : alert)),
    );
  }

  private handleError(message: string, error: unknown) {
    console.error(message, error);
    this._loading.set(false);
    return throwError(() => error);
  }
}
